import css from './notificationCenter.module.css'
import Tabs from '@/shared/Tabs'
import { useEffect, useRef, useState } from 'react'
import { TNotificationCenterTab } from './notificationCenter.types.ts'
import notificationCenterConstants from './notificationCenter.constants.ts'
import systemEventsApi from '@/pages/SystemEvents/systemEvents.api.ts'
import { INotification } from '@/services/Notification/notifications.types.ts'
import ThreeDotsLoader from '@/shared/Loaders/ThreeDotsLoader'
import FadeInPanel from '@/shared/Animated/FadeInPanel'
import notificationsApi from '@/services/Notification/notifications.api.ts'
import useObserveElementsByIntersection from './hooks/useObserveElementsByIntersection.ts'
import SystemEventsList from './components/SystemEventsList/index.tsx'
import NotificationsList from './components/NotificationsList/index.tsx'
import useUserNotifications from './hooks/useUserNotifications.ts'
import useSystemEvents from './hooks/useSystemEvents.ts'
import SystemEventsFilter from './components/SystemEventsFilter/index.tsx'
import EmptyPlaceholder from '@/shared/Common/EmptyPlaceholder/index.tsx'

interface IProps {
  open: boolean
}

const NotificationCenter = (props: IProps) => {
  const userNotificationsCtrl = useUserNotifications()

  const systemEventsCtrl = useSystemEvents()

  const observingElements = useRef<{ element: HTMLElement; id: INotification['id']; seen: INotification['seen'] }[]>([])
  const [selectedTab, setSelectedTab] = useState<TNotificationCenterTab>('userNotifications')

  useEffect(() => {
    if (!props.open) {
      userNotificationsCtrl.sendViewed()
      return
    }

    if (selectedTab === 'userNotifications') {
      systemEventsApi.list.abort()
      userNotificationsCtrl.updateNotifications()
    } else {
      notificationsApi.getPersonalisedNotifications.abort()
      systemEventsCtrl.updateSystemEvents()
    }
  }, [props.open, selectedTab])

  useObserveElementsByIntersection({
    elements: observingElements.current,
    refreshKey: userNotificationsCtrl.nextNotificationPageId,
    active: selectedTab === 'userNotifications',
    handleViewedNotification: (id) => userNotificationsCtrl.viewedRef.current.add(id),
    viewedNotifications: userNotificationsCtrl.viewedRef.current,
  })

  const renderContent = () => {
    if (
      (selectedTab === 'allSystemEvents' && systemEventsCtrl.systemEventsLoading) ||
      (selectedTab === 'userNotifications' &&
        !userNotificationsCtrl.notifications.length &&
        userNotificationsCtrl.notificationsLoading)
    ) {
      return (
        <div style={{ paddingTop: '100px', paddingRight: '24px' }}>
          <ThreeDotsLoader
            inscription={`Загрузка ${selectedTab === 'allSystemEvents' ? 'событий' : 'уведомлений'}...`}
          />
        </div>
      )
    }

    if (selectedTab === 'allSystemEvents') {
      return <SystemEventsList events={systemEventsCtrl.systemEvents} />
    }

    if (selectedTab === 'userNotifications' && !userNotificationsCtrl.notifications.length) {
      return (
        <div style={{ marginTop: '80px' }}>
          <EmptyPlaceholder inscription={`У вас пока пока нет\nни одного уведомления`} />
        </div>
      )
    }

    return (
      <NotificationsList
        loadMore={userNotificationsCtrl.loadNotifications}
        notifications={userNotificationsCtrl.notifications}
        isMore={userNotificationsCtrl.nextNotificationPageId !== null}
        onObserveCard={(el: HTMLElement | null, index: number, notification: INotification) =>
          (observingElements.current[index] = { element: el, id: notification.id, seen: notification.seen })
        }
      />
    )
  }

  const renderSystemEventsFilterBlock = () => {
    if (selectedTab !== 'allSystemEvents') return null
    return (
      <SystemEventsFilter
        debounceTimeoutMs={400}
        onSelectedEventTypeChange={systemEventsCtrl.setSelectedEventTypes}
        selectedEventType={systemEventsCtrl.selectedEventTypes}
        name="systemEventsFilter"
      />
    )
  }

  const handleTabSelect = (value: TNotificationCenterTab) => {
    userNotificationsCtrl.sendViewed()
    setSelectedTab(value)
  }

  return (
    <FadeInPanel open={props.open} className={css.container}>
      <Tabs
        tabs={notificationCenterConstants.cardTabs}
        onSelect={handleTabSelect}
        selectedTab={selectedTab}
        className={css.tabsContainer}
      />
      {renderSystemEventsFilterBlock()}
      <div className={css.notificationsWrapper}>{renderContent()}</div>
    </FadeInPanel>
  )
}

export default NotificationCenter
